import React, { useRef, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Upload, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { toast } from "sonner";
import { compressImage, formatBytes } from "@/lib/imageCompress";

export default function UploadPersonalDialog({ open, onOpenChange, clientId, docType, onCreated }) {
  const { t } = useI18n();
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !docType) return;
    setBusy(true);
    try {
      const compressed = await compressImage(file, { maxWidth: 1600, maxHeight: 1600, quality: 0.82 });
      if (compressed !== file && compressed.size < file.size) {
        const pct = Math.round(100 - (compressed.size * 100) / file.size);
        toast.info(`Bild optimiert: ${formatBytes(file.size)} → ${formatBytes(compressed.size)} (−${pct}%)`);
      }
      const fd = new FormData();
      fd.append("file", compressed);
      fd.append("client_id", clientId);
      fd.append("category", "personal");
      fd.append("doc_type", docType);
      await api.post("/documents/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Hochgeladen");
      onCreated && onCreated();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-none">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">
            {t.upload_doc}: {docType ? t.personal_types[docType] : ""}
          </DialogTitle>
        </DialogHeader>
        <div className="border-2 border-dashed border-border p-12 text-center" data-testid="personal-dropzone">
          <Upload className="w-8 h-8 mx-auto text-muted-foreground" strokeWidth={1.2} />
          <div className="text-sm text-muted-foreground mt-3">{t.drop_here}</div>
          <Button
            asChild disabled={busy}
            className="mt-4 rounded-none bg-foreground text-background gap-2"
          >
            <label htmlFor="personal-file-input" data-testid="personal-upload-btn" className={busy ? "pointer-events-none opacity-60" : "cursor-pointer"}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {busy ? "…" : t.upload}
            </label>
          </Button>
          <input ref={fileRef} id="personal-file-input" type="file" className="hidden"
            accept="image/*,application/pdf" onChange={onPickFile}
            data-testid="personal-file-input" />
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={() => onOpenChange(false)}>{t.cancel}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
